import { format } from "date-fns";
import { ArrowDown, ArrowUp, ArrowUpDown } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { CategoryBadge } from "./category-badge";

export interface InsuranceRow {
  id: number;
  address: string;
  insured: string;
  propertyType: string;
  propertyCategory: string | null;
  valuer: string;
  pocFrom: string | null;
  pocTo: string | null;
  status: "Active" | "Cancelled" | "Superseded";
  updatedAt: string;
}

type SortField = "id" | "address" | "insured" | "valuer" | "pocTo" | "status" | "updatedAt";
type SortDirection = "asc" | "desc";

interface InsuranceTableProps {
  records: InsuranceRow[];
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
}

const statusVariant: Record<string, "default" | "secondary" | "destructive"> = {
  Active: "default",
  Cancelled: "destructive",
  Superseded: "secondary",
};

function SortIcon({ field, current, direction }: { field: string; current: string; direction: SortDirection }) {
  if (field !== current) return <ArrowUpDown className="h-3.5 w-3.5 text-muted-foreground/50" />;
  return direction === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />;
}

function SortableHead({
  field,
  label,
  sortField,
  sortDirection,
  onSort,
  className,
}: {
  field: SortField;
  label: string;
  sortField: SortField;
  sortDirection: SortDirection;
  onSort: (field: SortField) => void;
  className?: string;
}) {
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(field)}
        className="flex items-center gap-1 hover:text-foreground transition-colors"
      >
        {label}
        <SortIcon field={field} current={sortField} direction={sortDirection} />
      </button>
    </TableHead>
  );
}

function formatCertPeriod(from: string | null, to: string | null): string {
  if (!from && !to) return "";
  const f = from ? format(new Date(from), "MMM yyyy") : "?";
  const t = to ? format(new Date(to), "MMM yyyy") : "?";
  return `${f} – ${t}`;
}

export function InsuranceTable({ records, sortField, sortDirection, onSort }: InsuranceTableProps): React.ReactElement {
  const navigate = useNavigate();

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <SortableHead
            field="id"
            label="ID"
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={onSort}
            className="w-16"
          />
          <SortableHead
            field="address"
            label="Address"
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <SortableHead
            field="insured"
            label="Insured"
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <SortableHead
            field="valuer"
            label="Valuer"
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <SortableHead
            field="pocTo"
            label="Certificate Period"
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <SortableHead
            field="status"
            label="Status"
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={onSort}
            className="w-24"
          />
          <SortableHead
            field="updatedAt"
            label="Modified"
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={onSort}
            className="w-28"
          />
        </TableRow>
      </TableHeader>
      <TableBody>
        {records.length === 0 ? (
          <TableRow>
            <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
              No records match your filters.
            </TableCell>
          </TableRow>
        ) : (
          records.map((record) => (
            <TableRow
              key={record.id}
              className="cursor-pointer"
              onClick={() => navigate(`/insurance/${record.id}`)}
              data-testid={`insurance-row-${record.id}`}
            >
              <TableCell className="font-medium text-primary">{record.id}</TableCell>
              <TableCell className="max-w-xs">
                <div className="flex items-center gap-2">
                  <span>{record.address}</span>
                  <CategoryBadge category={record.propertyCategory} />
                </div>
              </TableCell>
              <TableCell>{record.insured}</TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {record.valuer !== "—" ? record.valuer : ""}
              </TableCell>
              <TableCell className="text-xs whitespace-nowrap">
                {formatCertPeriod(record.pocFrom, record.pocTo)}
              </TableCell>
              <TableCell>
                <Badge variant={statusVariant[record.status]}>{record.status}</Badge>
              </TableCell>
              <TableCell className="text-muted-foreground text-xs whitespace-nowrap">
                {format(new Date(record.updatedAt), "dd MMM yyyy")}
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}
